import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import Powered360 from "@/components/Powered360";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const r = await login(email, password);
        setLoading(false);
        if (r.ok) {
            toast.success("Welcome back!");
            navigate(location.state?.from || "/dashboard");
        } else {
            setError(r.error);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain px-6">
            <div className="absolute top-6 left-6">
                <Logo size="md" />
            </div>
            <div className="w-full max-w-md">
                <div className="glass-card rounded-3xl p-8 md:p-10">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Welcome back</div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight mb-7">Log in to FoodGlow360</h1>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                                placeholder="you@restaurant.com"
                                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/40 transition"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Password</label>
                            <div className="mt-2 relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password-input"
                                    placeholder="••••••••"
                                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 pr-10 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/40 transition"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                                    aria-label="Toggle password visibility"
                                    data-testid="login-toggle-password"
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-2.5" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="btn-magic w-full py-3.5 rounded-xl text-white font-semibold mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Signing in..." : "Sign in"}
                        </button>
                    </form>
                    <div className="mt-7 text-sm text-zinc-400 text-center">
                        New to FoodGlow360?{" "}
                        <Link to="/signup" data-testid="login-signup-link" className="text-orange-400 hover:text-orange-300 font-medium">
                            Create account
                        </Link>
                    </div>
                </div>
                <div className="mt-6 flex justify-center">
                    <Powered360 variant="inline" />
                </div>
            </div>
        </div>
    );
}
